import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldAlert, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPageKeyForPath, isEditRoute } from "@/lib/pagePermissionMap";
import { usePagePermission } from "@/hooks/usePermissions";

interface Props { children: React.ReactNode; }

export function RoutePermissionGate({ children }: Props) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const pageKey = getPageKeyForPath(pathname);
  const needsEdit = isEditRoute(pathname);
  const { isLoading, canView, canEdit, isPrivileged } = usePagePermission(pageKey);

  if (!pageKey || isPrivileged) return <>{children}</>;
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  const allowed = needsEdit ? canEdit : canView;
  if (allowed) return <>{children}</>;

  const titleKey = needsEdit ? "permissions.deniedEdit.title" : "permissions.denied.title";
  const msgKey = needsEdit ? "permissions.deniedEdit.message" : "permissions.denied.message";

  return (
    <div className="flex flex-col items-center justify-center text-center py-20 px-4 gap-4">
      <div className="rounded-full bg-destructive/10 p-4">
        <ShieldAlert className="h-10 w-10 text-destructive" />
      </div>
      <h2 className="text-2xl font-bold">{t(titleKey, t("permissions.denied.title") as string)}</h2>
      <p className="text-muted-foreground max-w-md">{t(msgKey, t("permissions.denied.message") as string)}</p>
      <Button variant="outline" onClick={() => navigate(-1)}>
        <ArrowLeft className="me-2 h-4 w-4" />{t("permissions.denied.back")}
      </Button>
    </div>
  );
}
